import type { PatientDocument } from 'generated/api';

/**
 * Derives the "Referred to" rows shown under Consultants in the staff CPP.
 *
 * These rows are *derived from the patient's referral documents*, not stored in
 * health records. That is deliberate: a stored consultant entry has no link
 * back to the referral, so it can never show a date or a live status, and
 * writing them would drag in the health-record dedupe/tombstone machinery
 * (resurrecting rows staff deleted, clobbering hand-edited values). Deriving
 * costs nothing, self-heals when a document is deleted or archived, and shows
 * the full history immediately rather than only from deploy day.
 *
 * Staff-only by construction: the patient-facing summary reads
 * `healthRecords.consultants` instead, so nothing here reaches the patient app
 * or Carey's context.
 */

const DOCUMENT_TYPE_REFERRAL = 'Referral';

/** Mirrors REFERRAL_STATUS in the backend's src/api/constants.ts. */
const STATUS = {
  pending_send: 'Pending Send',
  pending_reply: 'Pending Reply',
  overdue: 'Overdue',
  completed: 'Completed',
  rejected: 'Rejected',
} as const;

/**
 * Status colours, mirroring the medication list's approach. Like medication
 * statuses these read as status, not branding, so they stay on the fixed Care&
 * palette in every tenant (see CPPMedicationListElement/style.scss).
 *
 * Colour is spent on rows that want attention. ~89% of resolved referrals end
 * up Approved, so colouring those would paint the whole panel one colour and
 * carry no information — they recede instead.
 */
export const REFERRAL_STATUS_CLASS = {
  rejected: 'referral-rejected',
  overdue: 'referral-overdue',
  pending: 'referral-pending',
  done: 'referral-done',
  muted: 'referral-muted',
} as const;

const CLASS_BY_STATUS: Record<string, string> = {
  [STATUS.rejected]: REFERRAL_STATUS_CLASS.rejected,
  [STATUS.overdue]: REFERRAL_STATUS_CLASS.overdue,
  [STATUS.pending_reply]: REFERRAL_STATUS_CLASS.pending,
  [STATUS.completed]: REFERRAL_STATUS_CLASS.done,
  [STATUS.pending_send]: REFERRAL_STATUS_CLASS.muted,
};

/**
 * Staff pick "Approved" in the status dropdown but it is stored as
 * "Completed"; show them the word they chose.
 */
const LABEL_BY_STATUS: Record<string, string> = {
  [STATUS.completed]: 'Approved',
};

export type ReferralRow = {
  id: string;
  name: string;
  specialty?: string;
  date?: number;
  status: string;
  statusLabel: string;
  colorClass: string;
};

const clean = (value?: string) => {
  const trimmed = (value ?? '').trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

class ReferralTrackingUtils {
  /**
   * One row per referral document, newest first.
   *
   * Repeat referrals to the same destination are NOT collapsed: each is a
   * distinct event with its own date and outcome, and collapsing them would
   * have to pick one status to display — misleading when one was approved and
   * a later one rejected.
   */
  getReferralRows(documents?: PatientDocument[]): ReferralRow[] {
    if (!documents?.length) return [];

    return documents
      .filter(
        (document) =>
          document.document_type === DOCUMENT_TYPE_REFERRAL && !!document.referral_status,
      )
      .map((document) => {
        const status = document.referral_status!;

        return {
          id: document.id!,
          // referral_provider is populated by the structured "long referral"
          // flow from the chosen contact. Paper-template referrals have no
          // send_to worth showing (the backend synthesises the name as 'N/A'),
          // so they fall back to the document title — which is the template
          // name, i.e. the clinic the NP picked.
          name: clean(document.referral_provider) ?? clean(document.title) ?? 'Referral',
          specialty: clean(document.referral_specialty),
          date: document.date,
          status,
          statusLabel: LABEL_BY_STATUS[status] ?? status,
          colorClass: CLASS_BY_STATUS[status] ?? REFERRAL_STATUS_CLASS.muted,
        };
      })
      .sort((a, b) => (b.date ?? 0) - (a.date ?? 0));
  }
}

export default new ReferralTrackingUtils();
